import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React from 'react';
import { t } from '../../../../types/translation/Translator';
import PackageSearchIcon from '../../../icons/Package/PackageSearchIcon';
import { Lot } from '../../../../types/lot/lot';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { CustomField, CustomFieldEntityType } from '../../../../types/custom_field/customField';
import { FilterProps, FilterStorageKey } from '../common/filter.util';
import { LotFilterInnerContent, LotFilterDropdownContent } from './DropdownContent';
import { BaseFilter, BaseFilterProps } from '../common/BaseFilter';
import { CustomFieldFilter } from '../CustomFieldFilter';
import { TagFilter } from '../TagFilter';
import { EntityType } from '../../../../types/comment';
import { TagRelation } from '../../../../types/tag';
import { Company } from '../../../../types/company';
import { CompanyPartnerFilter } from '../CompanyPartnerFilter/CompanyPartnerFilter';
import { ProductMasterDataFilter } from '../ProductMasterDataFilter';
import { TableSettings } from '../../../../types/table_settings/TableSettings';
import { lotTableFilter } from '../../../../ui/pages/Common/LotTable';

export class LotFilter extends BaseFilter<Lot> {
  productMasterData: Map<string, ProductMasterData>;
  customFields: Map<string, CustomField>;
  lots: Map<string, Lot>;
  tagRelations: Map<string, TagRelation>;
  company: Company;
  tableSettings?: TableSettings<Lot>;

  constructor(
    productMasterData: Map<string, ProductMasterData>,
    customFields: Map<string, CustomField>,
    lots: Map<string, Lot>,
    tagRelations: Map<string, TagRelation>,
    company: Company,
    tableSettings?: TableSettings<Lot>,
  ) {
    super(FilterStorageKey.LOT);
    this.productMasterData = productMasterData;
    this.customFields = customFields;
    this.lots = lots;
    this.tagRelations = tagRelations;
    this.company = company;
    this.tableSettings = tableSettings;
  }

  filterOptions(): BaseFilter<any>[] {
    return [
      ...(this.company.partner ? [new CompanyPartnerFilter()] : []),
      new ProductMasterDataFilter(
        this.customFields,
        this.tagRelations,
        this.company,
        undefined,
        pmd => pmd.lotManaged ?? false,
      ),
      ...CustomFieldFilter.for(CustomFieldEntityType.lot, this.lots, this.customFields),
      new TagFilter(EntityType.pmd),
    ];
  }

  toLabel = () => {
    return t(TranslationKey.lot).singular.label;
  };

  toInnerContent(props: BaseFilterProps): string | JSX.Element {
    return (
      <LotFilterInnerContent filter={this} filterProps={props.filterProps} setFilterProps={props.setFilterProps} />
    );
  }

  toDropdownContent(props: BaseFilterProps): React.JSX.Element {
    return (
      <LotFilterDropdownContent filter={this} filterProps={props.filterProps} setFilterProps={props.setFilterProps} />
    );
  }

  toIcon(): React.JSX.Element {
    return <PackageSearchIcon className={this.className} />;
  }

  toCount(props: BaseFilterProps): number {
    return props.filterProps.lot?.size || 0;
  }

  search(item: Lot, query: string) {
    return lotTableFilter(item, query, this.productMasterData, this.tableSettings);
  }

  filteredItems(items: Lot[], query: string, filterProps: FilterProps) {
    return items.filter(item => {
      return [
        LotFilter.filter(filterProps, item, this.customFields),
        ProductMasterDataFilter.filter(
          filterProps,
          this.productMasterData.get(item.productMasterDataId),
          this.customFields,
          this.tagRelations,
        ),
        this.search(item, query),
        TagFilter.filterBy(filterProps, EntityType.pmd, this.tagRelations, item.productMasterDataId),
      ].every(value => value === null || value);
    });
  }

  static filter(filterProps: FilterProps, item?: Lot, customFields: Map<string, CustomField> = new Map()) {
    if (item && filterProps.lot?.size && !filterProps.lot.has(item.id)) return false;
    if (item && filterProps.company?.size && !filterProps.company.has(item.companyId)) return false;
    return CustomFieldFilter.filter(filterProps, item, customFields, CustomFieldEntityType.lot);
  }
}
